import { FC, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { RiEyeLine, RiFilePdf2Line } from "react-icons/ri";
import DownloadIcon from "../../../../icons/DownloadIcon";

import { ClaimDocumentRef } from "../../../../../types/claims";
import { apiService } from "../../../../../services/apiService";
import { saveBlob } from "../../../../../services/fileDownloadService";
import { useCapabilities } from "../../../../../contexts/permissions/useCapabilities";
import useNotifier from "../../../../../hooks/useNotifier";

type DocumentAction = "view" | "download";

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const actionButtonSx = {
  height: 24,
  minWidth: 0,
  px: 1,
  fontSize: "0.75rem",
  textTransform: "none",
  color: "text.primary",
  borderColor: "divider",
} as const;

/**
 * A PDF attached to the claim (an appeal's document, a payment's correspondence)
 * to view in a new tab or download (with the download capability).
 */
const ClaimDocumentCard: FC<{
  document: ClaimDocumentRef;
  claimId: string;
}> = ({ document, claimId }) => {
  const { canDownloadAttachments } = useCapabilities();
  const notifier = useNotifier();
  const [pendingAction, setPendingAction] = useState<DocumentAction | null>(
    null,
  );

  // The document needs the session cookie, which a plain cross-origin link can't carry,
  // so both actions fetch the blob first
  const openDocument = async (action: DocumentAction) => {
    // Open the tab before awaiting: a window.open after the fetch gets popup-blocked
    const tab = action === "view" ? window.open("", "_blank") : null;
    setPendingAction(action);
    try {
      const response = await apiService.downloadClaimDocument(
        claimId,
        document.id,
        action === "view",
      );
      if (!response.success || !response.data) {
        tab?.close();
        notifier.showError(response.error || "Couldn't open the document.");
        return;
      }
      let { blob } = response.data;
      if (!blob.type || blob.type === "application/octet-stream") {
        // A generic type would make the tab download it instead of showing it
        blob = new Blob([blob], {
          type: document.contentType || "application/pdf",
        });
      }
      if (tab) {
        const url = URL.createObjectURL(blob);
        tab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        saveBlob(blob, response.data.filename || document.name);
      }
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        border: "1px solid",
        borderColor: "custom.cardBorder",
        borderRadius: "10px",
      }}
    >
      <Box sx={{ color: "error.main", display: "inline-flex", flexShrink: 0 }}>
        <RiFilePdf2Line size={22} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
          title={document.name}
        >
          {document.name}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          PDF · {formatFileSize(document.size)}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        size="small"
        onClick={() => openDocument("view")}
        disabled={!!pendingAction}
        aria-label="View document"
        startIcon={
          pendingAction === "view" ? (
            <CircularProgress size={12} color="inherit" />
          ) : (
            <RiEyeLine size={12} />
          )
        }
        sx={actionButtonSx}
      >
        View
      </Button>

      {canDownloadAttachments && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => openDocument("download")}
          disabled={!!pendingAction}
          aria-label="Download document"
          startIcon={
            pendingAction === "download" ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <DownloadIcon size={12} />
            )
          }
          sx={actionButtonSx}
        >
          Download
        </Button>
      )}
    </Box>
  );
};

export default ClaimDocumentCard;
